import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { detectOS } from '@/src/renderer/lib/platform/detection'
import { dispatchAppEvent } from '@/src/renderer/lib/events'
import type { ExternalSelectionProps } from './useTaskListInteraction'
import type { Section, Task } from '@/src/renderer/lib/db/models'
import { isLifeSpaceSwitchCombo } from './listNavKeyFilter'

const isMac = detectOS() === 'mac'

export type NavItemType = 'task' | 'section'

export interface NavItem {
  type: NavItemType
  id: string
}

/** Minimal segment shape needed by the navigation hook. */
export interface NavSegment {
  section: Section | null
  tasks: Task[]
  /** When true, this segment's section header is visually suppressed and skipped by nav. */
  hideHeader?: boolean
}

interface UseProjectNavigationOptions {
  segments: NavSegment[]
  onEscapeTop: () => void
  onDelete?: (ids: Set<string>) => void
  onDeleteSection?: (sectionId: string) => void
  onToggleComplete?: (ids: Set<string>) => void
  onToggleCollapse?: (sectionId: string) => void
  /** Cmd/Ctrl+Enter on a focused task: open inline creator below it */
  onCreateBelow?: (taskId: string) => void
  /** Cmd/Ctrl+Enter on a focused section: open creator at top of section */
  onCreateInSection?: (sectionId: string) => void
  /** Ref that is true while a drag-drop animation is in progress (blocks Enter-to-edit) */
  isDragAnimatingRef?: React.RefObject<boolean>
  /** When non-null, skip render-phase focus correction (a selection restore is pending) */
  skipFocusCorrectionRef?: React.RefObject<string | boolean | null>
  /** When true, Enter on a focused section does not open rename mode (virtual sections) */
  sectionsReadonly?: boolean
  /** When false (inactive TabStack slot), global keydown listener is skipped. */
  isActive?: boolean
}

/**
 * Unified keyboard navigation across all segments in a project.
 *
 * Builds a flat navigation list from segments (interleaving section headers
 * and tasks) and manages a single focusedNavIndex + selectedIds across the
 * entire project. SectionHeaders become inline-edit stops; tasks use the
 * existing TaskList/useTaskListInteraction in controlled mode.
 */
export function useProjectNavigation({
  segments,
  onEscapeTop,
  onDelete,
  onDeleteSection,
  onToggleComplete,
  onCreateBelow,
  onCreateInSection,
  onToggleCollapse,
  isDragAnimatingRef,
  skipFocusCorrectionRef,
  sectionsReadonly = false,
  isActive = true,
}: UseProjectNavigationOptions) {

  // ── Flat navigation list ──
  const flatNavList = useMemo<NavItem[]>(() => {
    const items: NavItem[] = []
    for (const seg of segments) {
      if (seg.section && !seg.hideHeader) {
        items.push({ type: 'section', id: seg.section.id })
        // Collapsed sections: header is a stop, but tasks are hidden
        if (seg.section.collapsed) continue
      }
      for (const task of seg.tasks) {
        items.push({ type: 'task', id: task.id })
      }
    }
    return items
  }, [segments])

  /** Walk backwards through flatNavList to find the section that contains the item at `idx`. */
  const getSectionForIndex = useCallback((list: NavItem[], idx: number): string | null => {
    for (let i = idx; i >= 0; i--) {
      if (list[i].type === 'section') return list[i].id
    }
    return null // unsectioned (above first section)
  }, [])

  // ── Core state ──
  const [focusedNavIndex, setFocusedNavIndex] = useState<number | null>(null)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null)
  const anchorRef = useRef<number | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)
  // Edit transfer: captures the target task ID on pointerdown (capture phase)
  // so cancelEditing can transfer editing atomically (A → B) without going
  // through null — avoiding the layout shift between pointerdown and click.
  const editTransferTargetRef = useRef<string | null>(null)
  // Skip the next click after an edit transfer — the click event may land on
  // the wrong element due to layout shift when the editing card collapses.
  const skipNextClickRef = useRef(false)
  // Stable ref for flatNavList so cancelEditing can read current values
  // without needing flatNavList in its dependency array.
  const flatNavListRef = useRef(flatNavList)
  flatNavListRef.current = flatNavList

  // ── Correct focusedNavIndex when flatNavList changes ──
  // Only calls setState when the index actually changes to avoid unnecessary re-renders.
  // Skip during pending selection restore — the restore effect will set the correct values.
  const prevFlatRef = useRef<NavItem[]>(flatNavList)
  if (prevFlatRef.current !== flatNavList && focusedNavIndex !== null && !skipFocusCorrectionRef?.current) {
    const prevItem = prevFlatRef.current[focusedNavIndex]
    if (prevItem) {
      const newIdx = flatNavList.findIndex(
        item => item.type === prevItem.type && item.id === prevItem.id
      )
      if (newIdx !== -1) {
        // Only update if index actually changed
        if (newIdx !== focusedNavIndex) {
          setFocusedNavIndex(newIdx)
          anchorRef.current = newIdx
        }
      } else {
        // Focused item removed — try next available
        const clampedIdx = Math.min(focusedNavIndex, flatNavList.length - 1)
        if (clampedIdx >= 0) {
          setFocusedNavIndex(clampedIdx)
          anchorRef.current = clampedIdx
          const newItem = flatNavList[clampedIdx]
          if (newItem?.type === 'task') {
            setSelectedIds(new Set([newItem.id]))
          } else {
            setSelectedIds(new Set())
          }
        } else {
          setFocusedNavIndex(null)
          anchorRef.current = null
          setSelectedIds(new Set())
        }
      }
    }
  }
  prevFlatRef.current = flatNavList

  // ── Which section is currently focused ──
  const focusedSectionId = useMemo(() => {
    if (focusedNavIndex === null) return null
    const item = flatNavList[focusedNavIndex]
    return item?.type === 'section' ? item.id : null
  }, [flatNavList, focusedNavIndex])

  // ── Which section is being edited (Enter on a focused section) ──
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null)

  // Clear editingSectionId when focus moves away from that section
  if (editingSectionId && editingSectionId !== focusedSectionId) {
    setEditingSectionId(null)
  }

  // ── Range selection helper (tasks + sections) ──
  const selectRange = useCallback((from: number, to: number): Set<string> => {
    const start = Math.min(from, to)
    const end = Math.max(from, to)
    const ids = new Set<string>()
    for (let i = start; i <= end; i++) {
      if (flatNavList[i]) ids.add(flatNavList[i].id)
    }
    return ids
  }, [flatNavList])

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set())
    setFocusedNavIndex(null)
    anchorRef.current = null
    setEditingTaskId(null)
    setEditingSectionId(null)
  }, [])

  // ── Navigate to a specific index ──
  const navigateTo = useCallback((idx: number, shift: boolean) => {
    setFocusedNavIndex(idx)
    const item = flatNavList[idx]

    if (shift) {
      if (anchorRef.current === null) anchorRef.current = idx
      setSelectedIds(selectRange(anchorRef.current, idx))
    } else {
      anchorRef.current = idx
      if (item?.type === 'task') {
        setSelectedIds(new Set([item.id]))
      } else {
        // Landed on section: clear task selection
        setSelectedIds(new Set())
      }
    }

    // No scrollIntoView for sections here — sequential arrow navigation keeps
    // adjacent items visible. TaskEntry handles its own focused-scroll for tasks.
  }, [flatNavList, selectRange])

  // ── Keyboard handler ──
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    // Don't handle keyboard when editing a task (TaskEntry handles its own keys)
    if (editingTaskId) return
    // Don't intercept keys when a dropdown/context menu is open
    if (document.querySelector('[role="menu"]')) return
    // Don't handle when focus is inside a task creator (expanded TaskEntry)
    if ((e.target as HTMLElement)?.closest?.('.task-entry--expanded')) return
    // Don't handle when focus is inside a section title input (SectionHeader handles its own keys).
    // Exception: Escape should clear navigation even when section input is focused.
    if ((e.target as HTMLElement)?.closest?.('.section-header__title-input')) {
      if (e.key === 'Escape') {
        e.preventDefault()
        clearSelection()
      }
      return
    }

    const len = flatNavList.length
    if (len === 0) return

    // Life-space switcher (Cmd/Ctrl+Alt+Arrow) claims these combos — don't
    // let them leak into list-navigation jump-to-start/end or page-jump.
    if (isLifeSpaceSwitchCombo(e)) return

    // Jump to start/end
    const isJumpToStart = isMac ? (e.metaKey && e.key === 'ArrowUp') : e.key === 'Home'
    const isJumpToEnd = isMac ? (e.metaKey && e.key === 'ArrowDown') : e.key === 'End'

    if (isJumpToStart || isJumpToEnd) {
      e.preventDefault()
      const targetIdx = isJumpToStart ? 0 : len - 1
      navigateTo(targetIdx, e.shiftKey)
      // Suppress scroll-spy so the smooth scroll doesn't trigger
      // section activation mid-way (which would fight the scroll).
      dispatchAppEvent('scroll-spy-suppress')
      // After suppressing, tell the sidebar which section we landed in.
      const targetSection = getSectionForIndex(flatNavList, targetIdx)
      dispatchAppEvent('scroll-spy-set-section', targetSection)
      setTimeout(() => {
        const scrollContainer = wrapperRef.current?.closest('.tab-stack__slot-scroll, .main-layout__scroll-wrap') as HTMLElement | null
        if (scrollContainer) {
          scrollContainer.scrollTo({ top: isJumpToStart ? 0 : scrollContainer.scrollHeight, behavior: 'instant' })
        }
      }, 0)
      return
    }

    // Alt+Arrow: jump 10 entries (page-style navigation, no escape to header).
    // When fewer than 10 items remain, jump to the first/last item.
    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && e.altKey && !e.metaKey && !e.ctrlKey) {
      e.preventDefault()
      const jumpSize = 10
      const dir = e.key === 'ArrowDown' ? 1 : -1
      let nextIdx: number
      if (focusedNavIndex === null) {
        nextIdx = e.key === 'ArrowDown' ? 0 : len - 1
      } else {
        nextIdx = Math.max(0, Math.min(len - 1, focusedNavIndex + dir * jumpSize))
      }
      // Signal TaskEntry's focus-scroll to use smooth behavior (tasks).
      const scrollWrap = wrapperRef.current?.closest('.tab-stack__slot-scroll, .main-layout__scroll-wrap')
      scrollWrap?.setAttribute('data-smooth-scroll', '')
      // Suppress scroll-spy so the smooth scroll doesn't trigger section activation
      dispatchAppEvent('scroll-spy-suppress')
      // Tell the sidebar which section we landed in.
      const targetSection = getSectionForIndex(flatNavList, nextIdx)
      dispatchAppEvent('scroll-spy-set-section', targetSection)
      navigateTo(nextIdx, e.shiftKey)
      // Always smooth-scroll to target after React render.
      // TaskEntry's focus-scroll handles tasks (reads the attr); this covers
      // section headers (no focus-scroll effect) and same-index boundary hits.
      // At boundaries (first/last), scroll to the very top/bottom of the page.
      setTimeout(() => {
        scrollWrap?.removeAttribute('data-smooth-scroll')
        const scrollContainer = scrollWrap as HTMLElement | null
        if (nextIdx === 0 && scrollContainer) {
          scrollContainer.scrollTo({ top: 0, behavior: 'smooth' })
        } else if (nextIdx === len - 1 && scrollContainer) {
          scrollContainer.scrollTo({ top: scrollContainer.scrollHeight, behavior: 'smooth' })
        } else {
          const item = flatNavList[nextIdx]
          if (!item) return
          const selector = item.type === 'task'
            ? `[data-task-id="${item.id}"]`
            : `[data-section-id="${item.id}"]`
          const el = wrapperRef.current?.querySelector(selector) as HTMLElement | null
          el?.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
        }
      }, 50)
      return
    }

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const dir = e.key === 'ArrowDown' ? 1 : -1

      // ArrowUp at position 0 → escape to header (only when something was selected)
      if (e.key === 'ArrowUp' && focusedNavIndex === 0 && !e.shiftKey) {
        clearSelection()
        onEscapeTop()
        return
      }

      let nextIdx: number
      const wasNull = focusedNavIndex === null
      if (wasNull) {
        // First navigation: land on the first/last item (section or task)
        nextIdx = e.key === 'ArrowDown' ? 0 : len - 1
      } else {
        nextIdx = Math.max(0, Math.min(len - 1, focusedNavIndex + dir))
      }

      navigateTo(nextIdx, e.shiftKey)

      // First navigation from nothing selected: ensure target is visible.
      // ArrowDown → scroll to top so the project title stays visible.
      // ArrowUp → scroll last item into view.
      if (wasNull) {
        requestAnimationFrame(() => {
          const scrollContainer = wrapperRef.current?.closest('.tab-stack__slot-scroll, .main-layout__scroll-wrap') as HTMLElement | null
          if (e.key === 'ArrowDown' && scrollContainer) {
            scrollContainer.scrollTo({ top: 0, behavior: 'smooth' })
          } else {
            const item = flatNavList[nextIdx]
            if (!item) return
            const selector = item.type === 'task'
              ? `[data-task-id="${item.id}"]`
              : `[data-section-id="${item.id}"]`
            const el = wrapperRef.current?.querySelector(selector) as HTMLElement | null
            el?.scrollIntoView({ block: 'nearest' })
          }
        })
      }
      return
    }

    // Section-specific shortcuts when a section header is focused
    if (focusedSectionId && focusedNavIndex !== null) {
      const seg = segments.find(s => s.section?.id === focusedSectionId)

      // ArrowLeft → collapse section (plain key only — Cmd/Ctrl/Alt are tab-switch shortcuts)
      if (e.key === 'ArrowLeft' && !e.metaKey && !e.ctrlKey && !e.altKey && seg?.section && !seg.section.collapsed) {
        e.preventDefault()
        onToggleCollapse?.(focusedSectionId)
        return
      }

      // ArrowRight → expand section (plain key only)
      if (e.key === 'ArrowRight' && !e.metaKey && !e.ctrlKey && !e.altKey && seg?.section?.collapsed) {
        e.preventDefault()
        onToggleCollapse?.(focusedSectionId)
        return
      }

      // Space on section: if tasks already selected → toggle complete, otherwise select all tasks
      if (e.code === 'Space' && seg) {
        e.preventDefault()
        const sectionTaskIds = seg.tasks.map(t => t.id)
        if (sectionTaskIds.length > 0) {
          if (selectedIds.size > 0) {
            onToggleComplete?.(new Set(selectedIds))
          } else {
            setSelectedIds(new Set(sectionTaskIds))
          }
        }
        return
      }
    }

    if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
      e.preventDefault()
      const allIds = new Set(flatNavList.map(i => i.id))
      setSelectedIds(allIds)
      setFocusedNavIndex(flatNavList.length > 0 ? flatNavList.length - 1 : null)
      anchorRef.current = flatNavList.length > 0 ? 0 : null
      return
    }

    if (e.code === 'Space' && selectedIds.size > 0) {
      e.preventDefault()
      // Filter to task IDs only — sections don't have a "complete" status
      const taskIds = new Set([...selectedIds].filter(id => flatNavList.find(i => i.id === id)?.type === 'task'))
      if (taskIds.size > 0) onToggleComplete?.(taskIds)
      return
    }

    // Cmd/Ctrl+Enter → task: inline creator below; section: creator at top of section
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && focusedNavIndex !== null) {
      const item = flatNavList[focusedNavIndex]
      if (item?.type === 'task' && onCreateBelow) {
        e.preventDefault()
        onCreateBelow(item.id)
        return
      }
      if (item?.type === 'section' && onCreateInSection) {
        e.preventDefault()
        onCreateInSection(item.id)
        return
      }
    }

    if (e.key === 'Enter' && focusedNavIndex !== null && !isDragAnimatingRef?.current) {
      const item = flatNavList[focusedNavIndex]
      if (item?.type === 'task') {
        e.preventDefault()
        setEditingTaskId(item.id)
        return
      }
      if (item?.type === 'section' && !sectionsReadonly) {
        e.preventDefault()
        setEditingSectionId(item.id)
        return
      }
      if (item?.type === 'section' && sectionsReadonly) {
        e.preventDefault()
        return
      }
    }

    // Backspace/Delete on a focused section header → delete section
    if ((e.key === 'Backspace' || e.key === 'Delete') && focusedNavIndex !== null && selectedIds.size === 0 && onDeleteSection) {
      const focusedItem = flatNavList[focusedNavIndex]
      if (focusedItem?.type === 'section') {
        e.preventDefault()
        const remainingItems = flatNavList.filter(item => item.id !== focusedItem.id)
        if (remainingItems.length === 0) {
          clearSelection()
        } else {
          const clampedIdx = Math.min(focusedNavIndex, remainingItems.length - 1)
          const nextItem = remainingItems[clampedIdx]
          if (nextItem?.type === 'task') setSelectedIds(new Set([nextItem.id]))
          else setSelectedIds(new Set())
        }
        onDeleteSection(focusedItem.id)
        requestAnimationFrame(() => wrapperRef.current?.focus({ preventScroll: true }))
        return
      }
    }

    if ((e.key === 'Backspace' || e.key === 'Delete') && selectedIds.size > 0) {
      // Split selectedIds into task IDs and section IDs
      const taskIds = new Set<string>()
      const sectionIds: string[] = []
      for (const id of selectedIds) {
        const item = flatNavList.find(i => i.id === id)
        if (item?.type === 'section') sectionIds.push(id)
        else taskIds.add(id)
      }
      if (taskIds.size === 0 && sectionIds.length === 0) return
      // Need at least one applicable handler
      if (taskIds.size > 0 && !onDelete && sectionIds.length === 0) return
      if (sectionIds.length > 0 && !onDeleteSection && taskIds.size === 0) return

      e.preventDefault()
      const allDeletedIds = new Set(selectedIds)
      const remainingItems = flatNavList.filter(item => !allDeletedIds.has(item.id))
      if (remainingItems.length === 0) {
        clearSelection()
      } else {
        const lastDeletedIdx = Math.max(
          ...[...allDeletedIds].map(id => flatNavList.findIndex(i => i.id === id))
        )
        const clampedIdx = Math.min(lastDeletedIdx, remainingItems.length - 1)
        const nextItem = remainingItems[clampedIdx]
        if (nextItem) {
          if (nextItem.type === 'task') setSelectedIds(new Set([nextItem.id]))
          else setSelectedIds(new Set())
        } else {
          clearSelection()
        }
      }
      // Delete tasks
      if (taskIds.size > 0 && onDelete) onDelete(taskIds)
      // Delete sections
      if (onDeleteSection) {
        for (const sectionId of sectionIds) onDeleteSection(sectionId)
      }
      requestAnimationFrame(() => wrapperRef.current?.focus({ preventScroll: true }))
      return
    }

    if (e.key === 'Escape') {
      e.preventDefault()
      clearSelection()
      return
    }
  }, [flatNavList, focusedNavIndex, selectedIds, editingTaskId, focusedSectionId, segments, navigateTo, selectRange, clearSelection, onEscapeTop, onDelete, onToggleComplete, onToggleCollapse, onCreateBelow, onCreateInSection, sectionsReadonly])

  // ── Section navigation callbacks (called from SectionHeader input) ──
  const handleSectionNavigateDown = useCallback((sectionId: string) => {
    const navIdx = flatNavList.findIndex(i => i.type === 'section' && i.id === sectionId)
    if (navIdx === -1) return
    const nextIdx = navIdx + 1
    if (nextIdx < flatNavList.length) {
      navigateTo(nextIdx, false)
      // If next item is a task, refocus the wrapper so keyboard nav works
      if (flatNavList[nextIdx]?.type === 'task') {
        requestAnimationFrame(() => wrapperRef.current?.focus({ preventScroll: true }))
      }
    }
  }, [flatNavList, navigateTo])

  const handleSectionNavigateUp = useCallback((sectionId: string) => {
    const navIdx = flatNavList.findIndex(i => i.type === 'section' && i.id === sectionId)
    if (navIdx === -1) return
    if (navIdx === 0) {
      // First item — escape to header
      clearSelection()
      onEscapeTop()
      return
    }
    const prevIdx = navIdx - 1
    navigateTo(prevIdx, false)
    // If prev item is a task, refocus the wrapper
    if (flatNavList[prevIdx]?.type === 'task') {
      requestAnimationFrame(() => wrapperRef.current?.focus({ preventScroll: true }))
    }
  }, [flatNavList, navigateTo, clearSelection, onEscapeTop])

  // ── Capture edit transfer target before TaskEntry's pointerdown clears editing ──
  // On capture phase: detect which task the user clicked (via data-task-id).
  // If a different task is clicked while editing, store the target so cancelEditing
  // can transfer editing atomically (A → B) in one re-render.
  const onPointerDownCapture = useCallback((e: React.PointerEvent) => {
    if (!editingTaskId) {
      editTransferTargetRef.current = null
      return
    }
    const targetEl = (e.target as HTMLElement).closest?.('[data-task-id]')
    const targetTaskId = targetEl?.getAttribute('data-task-id')
    if (targetTaskId && targetTaskId !== editingTaskId) {
      editTransferTargetRef.current = targetTaskId
    } else {
      editTransferTargetRef.current = null
    }
  }, [editingTaskId])

  // ── Mouse handlers ──
  const onTaskClick = useCallback((taskId: string, e: React.MouseEvent) => {
    // Skip click after an edit transfer — the click event may land on a wrong
    // element due to layout shift when the editing card collapsed.
    if (skipNextClickRef.current) {
      skipNextClickRef.current = false
      return
    }
    const navIdx = flatNavList.findIndex(i => i.type === 'task' && i.id === taskId)
    if (navIdx === -1) return

    if (e.shiftKey && anchorRef.current !== null) {
      setFocusedNavIndex(navIdx)
      const range = selectRange(anchorRef.current, navIdx)
      if (e.metaKey || e.ctrlKey) {
        setSelectedIds(prev => {
          const next = new Set(prev)
          range.forEach(id => next.add(id))
          return next
        })
      } else {
        setSelectedIds(range)
      }
      wrapperRef.current?.focus({ preventScroll: true })
      return
    }

    if (e.metaKey || e.ctrlKey) {
      setSelectedIds(prev => {
        const next = new Set(prev)
        if (next.has(taskId)) next.delete(taskId)
        else next.add(taskId)
        return next
      })
      setFocusedNavIndex(navIdx)
    } else {
      setSelectedIds(new Set([taskId]))
      setFocusedNavIndex(navIdx)
    }

    anchorRef.current = navIdx
    wrapperRef.current?.focus({ preventScroll: true })
  }, [flatNavList, selectRange])

  const onTaskDoubleClick = useCallback((taskId: string) => {
    if (isDragAnimatingRef?.current) return
    const navIdx = flatNavList.findIndex(i => i.type === 'task' && i.id === taskId)
    if (navIdx === -1) return
    setSelectedIds(new Set([taskId]))
    setFocusedNavIndex(navIdx)
    anchorRef.current = navIdx
    setEditingTaskId(taskId)
  }, [flatNavList])

  const cancelEditing = useCallback(() => {
    // Edit transfer: if a different task was clicked (detected in onPointerDownCapture),
    // transfer editing directly (A → B) without going through null. This avoids
    // layout shift when the editing card collapses and subsequent elements shift up.
    const transferTarget = editTransferTargetRef.current
    editTransferTargetRef.current = null

    if (transferTarget) {
      const navList = flatNavListRef.current
      const navIdx = navList.findIndex(i => i.type === 'task' && i.id === transferTarget)
      if (navIdx !== -1) {
        setEditingTaskId(transferTarget)
        setSelectedIds(new Set([transferTarget]))
        setFocusedNavIndex(navIdx)
        anchorRef.current = navIdx
        skipNextClickRef.current = true
        return
      }
    }

    setEditingTaskId(null)
    requestAnimationFrame(() => wrapperRef.current?.focus({ preventScroll: true }))
  }, [])

  // ── Click outside → clear selection ──
  // Uses pointerdown (not mousedown) so it fires BEFORE Radix's DismissableLayer
  // removes the dropdown portal from the DOM. This allows the querySelector check
  // to find the dropdown and skip clearing when one is open.
  useEffect(() => {
    const handleClickOutside = (e: PointerEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        // Don't clear if clicking inside a Radix portal or if any Radix dropdown is open.
        // With modal={true}, Radix intercepts clicks via an invisible layer, so the
        // event target won't be inside the wrapper even for clicks visually inside it.
        const target = e.target as Node
        const el = target instanceof Element ? target : target.parentElement
        if (el?.closest('[data-radix-popper-content-wrapper]')) return
        if (document.querySelector('[data-radix-popper-content-wrapper]')) return
        // Don't clear if clicking inside a TaskDropdown portal or its backdrop overlay.
        if (el?.closest('[data-task-dropdown], [data-task-dropdown-backdrop]')) return
        if (document.querySelector('[data-task-dropdown]')) return
        // Don't clear on clicks in navigation chrome (tabs, sidebars, titlebar) —
        // those are navigation intents, not deselection. Clearing here would
        // wipe the selection right before the page unmounts on a tab switch,
        // defeating per-tab selection persistence.
        if (el?.closest('.tab-bar, .standard-sidebar, .life-spaces-sidebar, .right-sidebar, .titlebar')) return
        clearSelection()
      }
    }
    document.addEventListener('pointerdown', handleClickOutside)
    return () => document.removeEventListener('pointerdown', handleClickOutside)
  }, [clearSelection])

  // Refs for volatile values — read inside global keydown handler to avoid re-registration
  const selectedIdsRef = useRef(selectedIds)
  selectedIdsRef.current = selectedIds
  const focusedNavIndexRef = useRef(focusedNavIndex)
  focusedNavIndexRef.current = focusedNavIndex
  const editingTaskIdRef = useRef(editingTaskId)
  editingTaskIdRef.current = editingTaskId

  // ── Global arrow keys → focus list when nothing is selected ──
  useEffect(() => {
    if (!isActive) return
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if (e.defaultPrevented) return
      if (editingTaskIdRef.current) return
      const navList = flatNavListRef.current
      if (navList.length === 0) return

      const tag = (e.target as HTMLElement)?.tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      // Don't intercept keys when a dropdown/context menu is open
      if (document.querySelector('[role="menu"]')) return

      // Cmd/Ctrl+A → select all items (tasks + sections)
      if ((e.metaKey || e.ctrlKey) && e.key === 'a') {
        e.preventDefault()
        const allIds = new Set(navList.map(i => i.id))
        setSelectedIds(allIds)
        setFocusedNavIndex(navList.length > 0 ? navList.length - 1 : null)
        anchorRef.current = navList.length > 0 ? 0 : null
        wrapperRef.current?.focus({ preventScroll: true })
        return
      }

      if (selectedIdsRef.current.size > 0 || focusedNavIndexRef.current !== null) return

      // Life-space switcher (Cmd/Ctrl+Alt+Arrow) — don't initiate list nav.
      if (isLifeSpaceSwitchCombo(e)) return

      if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !e.shiftKey) {
        // Cmd/Ctrl+Arrow → jump to first/last
        const isJumpStart = isMac ? (e.metaKey && e.key === 'ArrowUp') : (e.key === 'ArrowUp' && e.ctrlKey)
        const isJumpEnd = isMac ? (e.metaKey && e.key === 'ArrowDown') : (e.key === 'ArrowDown' && e.ctrlKey)
        // Alt+Arrow → jump 10 entries
        const isPageJump = e.altKey && !e.metaKey && !e.ctrlKey

        /** Navigate to idx, focus wrapper, and scroll the target into view. */
        const navigateAndScroll = (idx: number, scrollToTop: boolean) => {
          navigateTo(idx, false)
          wrapperRef.current?.focus({ preventScroll: true })
          // Suppress scroll-spy so the smooth scroll doesn't trigger
          // section activation mid-way (which would fight the scroll).
          dispatchAppEvent('scroll-spy-suppress')
          // Tell the sidebar which section we landed in.
          const targetSection = getSectionForIndex(navList, idx)
          dispatchAppEvent('scroll-spy-set-section', targetSection)
          requestAnimationFrame(() => {
            // TabStack rollout moved the scroll container from the shared
            // `.main-layout__scroll-wrap` to per-slot `.tab-stack__slot-scroll`.
            // Match the new selector first; fall back to the old one so
            // non-TabStack render paths (if any) keep working.
            const scrollContainer = wrapperRef.current?.closest(
              '.tab-stack__slot-scroll, .main-layout__scroll-wrap',
            ) as HTMLElement | null
            if (scrollToTop && scrollContainer) {
              scrollContainer.scrollTo({ top: 0, behavior: 'smooth' })
              return
            }
            // Jump to last item: scroll the container all the way down so
            // the row ends up above the bottom fade overlay, not under it.
            // `scroll-padding-bottom` on the slot-scroll already makes
            // scrollIntoView respect the fade — but scrolling to scrollHeight
            // is belt-and-suspenders safe regardless of block:'nearest' quirks.
            if (idx === navList.length - 1 && scrollContainer) {
              scrollContainer.scrollTo({
                top: scrollContainer.scrollHeight - scrollContainer.clientHeight,
                behavior: 'smooth',
              })
              return
            }
            const item = navList[idx]
            if (!item) return
            const selector = item.type === 'task'
              ? `[data-task-id="${item.id}"]`
              : `[data-section-id="${item.id}"]`
            const el = wrapperRef.current?.querySelector(selector) as HTMLElement | null
            el?.scrollIntoView({ block: 'nearest' })
          })
        }

        if (isJumpStart || isJumpEnd) {
          e.preventDefault()
          const idx = isJumpStart ? 0 : navList.length - 1
          navigateAndScroll(idx, isJumpStart)
          return
        }

        if (isPageJump) {
          e.preventDefault()
          const jumpSize = 10
          const idx = e.key === 'ArrowDown'
            ? Math.min(jumpSize - 1, navList.length - 1)
            : navList.length - 1
          navigateAndScroll(idx, e.key === 'ArrowDown')
          return
        }

        // Plain Arrow → land on first/last item
        if (!e.ctrlKey && !e.metaKey && !e.altKey) {
          e.preventDefault()
          const idx = e.key === 'ArrowDown' ? 0 : navList.length - 1
          navigateAndScroll(idx, e.key === 'ArrowDown')
        }
      }
    }
    window.addEventListener('keydown', handleGlobalKeyDown)
    return () => window.removeEventListener('keydown', handleGlobalKeyDown)
  }, [isActive, navigateTo])

  // ── State derivation for child components ──
  const getSectionProps = useCallback((sectionId: string) => ({
    focused: focusedSectionId === sectionId,
    selected: selectedIds.has(sectionId),
    editing: editingSectionId === sectionId,
    onEditEnd: () => setEditingSectionId(null),
  }), [focusedSectionId, selectedIds, editingSectionId])

  // Build ExternalSelectionProps for a given segment's task list
  const getExternalSelection = useCallback((segmentTaskIds: string[]): ExternalSelectionProps => {
    const segIdSet = new Set(segmentTaskIds)
    const focusedItem = focusedNavIndex !== null ? flatNavList[focusedNavIndex] : null
    const focusedTaskId = (focusedItem?.type === 'task' && segIdSet.has(focusedItem.id))
      ? focusedItem.id
      : null

    return {
      selectedIds,
      focusedTaskId,
      onSelect: onTaskClick,
      onDoubleClick: onTaskDoubleClick,
      editingTaskId: (editingTaskId && segIdSet.has(editingTaskId)) ? editingTaskId : null,
      onCancelEdit: cancelEditing,
      onAutoSelect: (taskId: string) => {
        const navIdx = flatNavList.findIndex(i => i.type === 'task' && i.id === taskId)
        if (navIdx === -1) return
        setSelectedIds(new Set([taskId]))
        setFocusedNavIndex(navIdx)
        anchorRef.current = navIdx
        wrapperRef.current?.focus({ preventScroll: true })
      },
      onAutoCollapse: (taskId: string) => {
        const navIdx = flatNavList.findIndex(i => i.type === 'task' && i.id === taskId)
        if (navIdx === -1) return
        setEditingTaskId(taskId)
        setSelectedIds(new Set([taskId]))
        setFocusedNavIndex(navIdx)
        anchorRef.current = navIdx
      },
    }
  }, [flatNavList, focusedNavIndex, selectedIds, editingTaskId, onTaskClick, onTaskDoubleClick, cancelEditing])

  // Imperative: set focus index AND anchor in sync (for external callers like useDuplicateItems)
  const setFocusedNavIndexWithAnchor = useCallback((idx: number | null) => {
    setFocusedNavIndex(idx)
    anchorRef.current = idx
  }, [])

  // Imperative methods
  const selectTask = useCallback((taskId: string) => {
    const navIdx = flatNavList.findIndex(i => i.type === 'task' && i.id === taskId)
    if (navIdx === -1) return
    setSelectedIds(new Set([taskId]))
    setFocusedNavIndex(navIdx)
    anchorRef.current = navIdx
    wrapperRef.current?.focus({ preventScroll: true })
  }, [flatNavList])

  const selectSection = useCallback((sectionId: string) => {
    if (editingTaskId) setEditingTaskId(null)
    const navIdx = flatNavList.findIndex(i => i.type === 'section' && i.id === sectionId)
    if (navIdx === -1) return
    setSelectedIds(new Set())
    setFocusedNavIndex(navIdx)
    anchorRef.current = navIdx
    wrapperRef.current?.focus({ preventScroll: true })
  }, [flatNavList, editingTaskId])

  // Select all tasks belonging to a section (triggered by double-click on section header)
  const selectSectionTasks = useCallback((sectionId: string) => {
    const sectionNavIdx = flatNavList.findIndex(i => i.type === 'section' && i.id === sectionId)
    if (sectionNavIdx === -1) return
    const ids: string[] = []
    let firstIdx: number | null = null
    for (let i = sectionNavIdx + 1; i < flatNavList.length; i++) {
      const item = flatNavList[i]
      if (item.type === 'section') break
      if (item.type === 'task') {
        ids.push(item.id)
        if (firstIdx === null) firstIdx = i
      }
    }
    if (ids.length === 0) return
    setSelectedIds(new Set(ids))
    setFocusedNavIndex(sectionNavIdx)
    anchorRef.current = sectionNavIdx
    wrapperRef.current?.focus({ preventScroll: true })
  }, [flatNavList])

  const focusFirst = useCallback(() => {
    if (flatNavList.length === 0) return
    // Navigate to the very first item (task or section header)
    navigateTo(0, false)
    wrapperRef.current?.focus({ preventScroll: true })
  }, [flatNavList, navigateTo])

  // Tab/Shift+Tab in expanded mode: save and move edit to adjacent task
  const navigateEdit = useCallback((fromTaskId: string, direction: 'next' | 'prev') => {
    const navIdx = flatNavList.findIndex(i => i.type === 'task' && i.id === fromTaskId)
    if (navIdx === -1) return

    const dir = direction === 'next' ? 1 : -1
    let targetIdx = navIdx + dir
    while (targetIdx >= 0 && targetIdx < flatNavList.length) {
      if (flatNavList[targetIdx].type === 'task') {
        const targetId = flatNavList[targetIdx].id
        setEditingTaskId(targetId)
        setSelectedIds(new Set([targetId]))
        setFocusedNavIndex(targetIdx)
        anchorRef.current = targetIdx
        return
      }
      targetIdx += dir
    }
    // No more tasks in this direction — just close editing
  }, [flatNavList])

  return {
    wrapperRef,
    flatNavList,
    focusedNavIndex,
    selectedIds,
    editingTaskId,
    focusedSectionId,
    editingSectionId,
    getSectionProps,
    getExternalSelection,
    onTaskClick,
    onTaskDoubleClick,
    handleSectionNavigateDown,
    handleSectionNavigateUp,
    setEditingTaskId,
    cancelEditing,
    clearSelection,
    selectTask,
    selectSection,
    selectSectionTasks,
    setFocusedNavIndex,
    setFocusedNavIndexWithAnchor,
    setSelectedIds,
    focusFirst,
    navigateEdit,
    onKeyDown,
    onPointerDownCapture,
  }
}
